import { useEffect, useState, useCallback } from "react";
import {
  Slide,
  Typography,
  Box,
  Button,
  Paper,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  CircularProgress,
} from "@mui/material";
import NotificationsActiveIcon from "@mui/icons-material/NotificationsActive";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import axios from "axios";
import {
  ensureWebFcmToken,
  onForegroundMessage,
} from "./FirebaseMessaging";
import {
  DAILY_DIGEST_TYPE,
  dispatchHomeRefreshForNotification,
  formatDailyDigestBody,
  navigateForNotification,
  normalizeNotificationData,
} from "./notificationRoutes";
import StorageFactory from "../StorageFactory";
import {
  SESSION_USER_ID_KEY,
  SESSION_USER_ROLES_PERMISSIONS,
  SESSION_USER_TYPE_KEY,
} from "../VMConstants";
import { API_GET_USER_UPDATES } from "../URLConstants";
import { isEmpty } from "../UtilHelper";
import { useNavigate } from "react-router-dom";
import NotificationPrompt from "../../LoginRegistration/NotificationPrompt";
import { openNotificationSettings } from "./browserNotificationUtils";

const SlideUp = (props) => <Slide direction="up" {...props} />;

const resolveNotificationCopy = (payload, data) => {
  const title =
    payload?.notification?.title || data.title || "Venue Manager";
  let body = payload?.notification?.body || data.body || "";
  if (!body && data.type === DAILY_DIGEST_TYPE) {
    body = formatDailyDigestBody(data);
  }
  return { title, body };
};

const FCMListener = () => {
  const [notification, setNotification] = useState(null);
  const [open, setOpen] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [promptOpen, setPromptOpen] = useState(false);
  const [permission, setPermission] = useState(
    typeof Notification !== "undefined" ? Notification.permission : "default"
  );

  const sessionFactory = new StorageFactory("session");
  const userID = sessionFactory.get(SESSION_USER_ID_KEY);
  const userType = sessionFactory.get(SESSION_USER_TYPE_KEY);
  const navigate = useNavigate();

  const showTeamUpdateDialog = useCallback((title, body) => {
    setNotification({ title, body, data: { type: "UPDATE_TEAM_MEMBER" } });
    setDialogOpen(true);
  }, []);

  const handlePayload = useCallback(
    (payload) => {
      const data = normalizeNotificationData(payload?.data || {});
      const { title, body } = resolveNotificationCopy(payload, data);

      if (
        data.type === "UPDATE_TEAM_MEMBER" &&
        userType?.toLowerCase() !== "admin"
      ) {
        showTeamUpdateDialog(title, body);
        return;
      }

      setNotification({ title, body, data });
      setOpen(true);
      dispatchHomeRefreshForNotification(data);
    },
    [showTeamUpdateDialog, userType]
  );

  const handleNotificationTap = useCallback(
    (data) => {
      const normalized = normalizeNotificationData(data || {});
      if (
        normalized.type === "UPDATE_TEAM_MEMBER" &&
        userType?.toLowerCase() !== "admin"
      ) {
        showTeamUpdateDialog(
          notification?.title || "Alert",
          notification?.body || ""
        );
        return;
      }
      setOpen(false);
      navigateForNotification(navigate, normalized);
    },
    [navigate, notification, showTeamUpdateDialog, userType]
  );

  /* Token sync + foreground listener + SW click bridge + permission prompt */
  useEffect(() => {
    if (!userID) return undefined;

    const currentPermission =
      typeof Notification !== "undefined" ? Notification.permission : "denied";
    setPermission(currentPermission);

    let promptTimer;
    if (currentPermission === "granted") {
      ensureWebFcmToken({ requestPermission: false }).catch((err) =>
        console.error("Firebase token error:", err)
      );
    } else {
      promptTimer = setTimeout(() => setPromptOpen(true), 1500);
    }

    let unsubscribe = () => {};
    onForegroundMessage((payload) => {
      console.log("FCM foreground message:", payload);
      handlePayload(payload);
    }).then((unsub) => {
      unsubscribe = typeof unsub === "function" ? unsub : () => {};
    });

    const onSwMessage = (event) => {
      if (event.data?.type === "FCM_NOTIFICATION_CLICK") {
        handleNotificationTap(event.data.data || {});
      }
    };
    navigator.serviceWorker?.addEventListener("message", onSwMessage);

    return () => {
      if (promptTimer) clearTimeout(promptTimer);
      unsubscribe();
      navigator.serviceWorker?.removeEventListener("message", onSwMessage);
    };
  }, [userID, handlePayload, handleNotificationTap]);

  const handleEnableNotifications = useCallback(async () => {
    const token = await ensureWebFcmToken({ requestPermission: true });
    const next =
      typeof Notification !== "undefined" ? Notification.permission : "denied";
    setPermission(next);
    if (next === "granted") {
      setPromptOpen(false);
      if (!token) {
        console.error("Notification permission granted but FCM token missing");
      }
    }
  }, []);

  useEffect(() => {
    if (open) {
      const timer = setTimeout(() => setOpen(false), 5000);
      return () => clearTimeout(timer);
    }
  }, [open]);

  const handleDialogOk = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API_GET_USER_UPDATES}?user_id=${userID}`);
      if (res.data?.status_code === 200 && res.data?.data?.permissions) {
        let updatedPermissions = [];
        if (!isEmpty(res.data?.data?.permissions)) {
          updatedPermissions = res.data.data.permissions.map(
            (perm) => perm.permission_id
          );
        }
        sessionFactory.add(SESSION_USER_ROLES_PERMISSIONS, updatedPermissions);
      }
      navigate(0);
    } catch (error) {
      console.error("Error calling API_GET_USER_UPDATES:", error);
    } finally {
      setLoading(false);
      setDialogOpen(false);
    }
  };

  return (
    <>
      <NotificationPrompt
        open={Boolean(userID) && promptOpen}
        permission={permission}
        onEnable={handleEnableNotifications}
        onOpenBrowserSettings={openNotificationSettings}
        onClose={() => setPromptOpen(false)}
      />

      <SlideUp in={open && Boolean(notification)} mountOnEnter unmountOnExit>
        <Paper
          elevation={6}
          onClick={() =>
            notification?.data && handleNotificationTap(notification.data)
          }
          sx={{
            position: "fixed",
            bottom: 24,
            right: 24,
            width: 340,
            display: "flex",
            p: 2,
            borderRadius: 3,
            boxShadow: "0px 6px 20px rgba(0,0,0,0.2)",
            backgroundColor: "background.paper",
            zIndex: 9999,
            cursor: "pointer",
          }}
        >
          <Box
            sx={{
              width: 48,
              height: 48,
              backgroundColor: "primary.main",
              color: "#fff",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              borderRadius: "50%",
              mr: 2,
            }}
          >
            <NotificationsActiveIcon />
          </Box>

          <Box flex={1}>
            <Typography variant="subtitle1" sx={{ fontWeight: "bold" }}>
              {notification?.title}
            </Typography>
            <Typography
              variant="body2"
              sx={{ mt: 0.5, color: "text.secondary" }}
            >
              {notification?.body}
            </Typography>
          </Box>
        </Paper>
      </SlideUp>

      <Dialog
        open={dialogOpen}
        onClose={(event, reason) => {
          if (reason === "backdropClick") return;
          if (reason === "escapeKeyDown") return;
          !loading && setDialogOpen(false);
        }}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1,
            fontWeight: "bold",
            color: "error.main",
          }}
        >
          <WarningAmberIcon color="error" /> Alert
        </DialogTitle>
        <DialogContent>
          {loading ? (
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                py: 3,
              }}
            >
              <CircularProgress />
              <Typography variant="body2" sx={{ mt: 2 }}>
                Updating permissions...
              </Typography>
            </Box>
          ) : (
            <Typography sx={{ color: "text.primary" }}>
              {notification?.body}
            </Typography>
          )}
        </DialogContent>
        {!loading && (
          <DialogActions>
            <Button
              onClick={handleDialogOk}
              variant="contained"
              color="primary"
              autoFocus
            >
              OK
            </Button>
          </DialogActions>
        )}
      </Dialog>
    </>
  );
};

export default FCMListener;
